import { useId, useMemo } from 'react';
import clsx from 'clsx';
import { formatCurrency } from '@budget/core';
import { useDashboard } from '../../data/useDashboard';
import { dedupeSnapshotsByDate, useNetWorthHistory } from '../../data/useNetWorthHistory';
import { CardFigure, HomeCard } from './HomeCard';

// Household net worth with a year of history behind it, opening the finance
// dashboard. The line runs off the card's left edge (more history that way)
// and stops at today's value, marked with the glowing dot.

const SPARK_DAYS = 365;
// The sparkline's box: inset from the top past the title row, and from the
// right so the line ends on the card at today.
const SPARK_INSET = 'absolute left-0 top-10 right-3 bottom-0 lg:top-11 lg:right-[18px]';
// The line sits in the top 30% of its box, leaving the lower part of the card
// to the fading fill and the number — even at the line's lowest point there's
// clear space above the big figure on the short phone card.
const LINE_TOP = 6;
const LINE_BOTTOM = 30;

interface SparkPoint {
  x: number; // 0..200
  y: number; // 0..100
}

function sparkPoints(values: Array<{ t: number; v: number }>): SparkPoint[] {
  if (values.length < 2) return [];
  const t0 = values[0].t;
  const t1 = values[values.length - 1].t;
  let min = Infinity;
  let max = -Infinity;
  for (const { v } of values) {
    min = Math.min(min, v);
    max = Math.max(max, v);
  }
  const span = max - min || 1;
  return values.map(({ t, v }) => ({
    x: t1 === t0 ? 0 : ((t - t0) / (t1 - t0)) * 200,
    y: LINE_BOTTOM - ((v - min) / span) * (LINE_BOTTOM - LINE_TOP),
  }));
}

export function FinanceCard() {
  const { data } = useDashboard();
  const { snapshots } = useNetWorthHistory();
  const current = data?.netWorth.netWorth.household;

  const points = useMemo(() => {
    const cutoff = Date.now() - SPARK_DAYS * 86_400_000;
    const series = dedupeSnapshotsByDate(snapshots ?? [])
      .map((s) => ({ t: Date.parse(`${s.date}T00:00:00Z`), v: s.household }))
      .filter((p) => p.t >= cutoff);
    // Pin the right end to the live sheet figure, as the dashboard chart does.
    if (typeof current === 'number') series.push({ t: Date.now(), v: current });
    return sparkPoints(series);
  }, [snapshots, current]);

  const fadeId = `nw-fade-${useId().replace(/:/g, '')}`;
  const line = points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const last = points[points.length - 1];

  return (
    <HomeCard
      title="Finance"
      wash="--berg-green-soft"
      to="/finance"
      ariaLabel={
        typeof current === 'number'
          ? `Finance: household net worth ${formatCurrency(current, 'USD', 'en-US', 0)}`
          : 'Finance'
      }
      decoration={
        last && (
          <>
            {/* Inset from the right so the line ends on the card at today; the
              mask fades the older history out toward the left edge. */}
            <span
              aria-hidden
              className={clsx(
                SPARK_INSET,
                '[mask-image:linear-gradient(to_right,transparent,black_80%)]'
              )}
            >
              <svg
                viewBox="0 0 200 100"
                preserveAspectRatio="none"
                className="absolute inset-0 w-full h-full animate-draw-in"
              >
                <defs>
                  <linearGradient id={fadeId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="var(--berg-green)" stopOpacity="0.18" />
                    <stop offset="0.65" stopColor="var(--berg-green)" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <polygon points={`0,100 ${line} 200,100`} fill={`url(#${fadeId})`} />
                <polyline
                  points={line}
                  fill="none"
                  stroke="var(--berg-green)"
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                  className="line-glow"
                />
              </svg>
            </span>
            {/* Today's dot sits on the box's right edge, so it lives in an
              unmasked twin of the box — inside the mask its right half and
              glow were clipped. */}
            <span aria-hidden className={SPARK_INSET}>
              <span
                className="absolute w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-full dot-shared animate-fade-in-late"
                style={{ left: '100%', top: `${last.y}%` }}
              />
            </span>
          </>
        )
      }
    >
      <CardFigure
        unit={typeof current === 'number' && current < 0 ? '−$' : '$'}
        value={
          typeof current === 'number' ? Math.abs(Math.round(current)).toLocaleString('en-US') : '–'
        }
        caption="Household net worth"
      />
    </HomeCard>
  );
}
